import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { propOr, pipe, map, sum } from 'ramda';

import {
  getExtrasTotal,
  getExtrasFees,
  getTrainFees,
  getProvisionalTicketTotal,
  getTicketTotal,
  getTrainLoyaltyPoints,
  getHotelsPrice,
  getExtrasWithCode,
  getFeeAmount,
  getHotelsMargin,
  getTrainDeprecatedMargin,
  getDbFeeAmount
} from 'utils/refunds';
import formatReason from 'utils/formatReason';
import { marginTariff } from 'constants/tariffCodes';
import { legShape, hotelsShape, feeShape, userShape } from 'constants/propTypes';
import { Column } from 'components/shared/Layout';
import FeeBreakdownModal from 'components/shared/FeeBreakdownModal';
import PricesAndPointsSummary from 'components/shared/PricesAndPointsSummary';
import FormattedCurrency from 'components/ui/FormattedCurrency';
import OverrideFeesCta from 'components/shared/OverrideFeesCta';
import AlertBox from 'components/ui/AlertBox';

import {
  StyleHeaderItems,
  StyledBody,
  StyleHeaderItem
} from 'components/ui/BookingPathReviewBookingSummary/BookingPathReviewBookingSummary.styles';

import SectionHeaderTitle from 'components/ui/BookingPathReviewBookingSummary/SectionHeaderTitle';

import SectionHeaderBar from 'components/ui/BookingPathReviewBookingSummary/SectionHeaderBar';
import { isTravelAgent } from 'utils/user';
import { EUROSTAR_CARRIERS } from 'constants/serviceTypeCodes';
import {
  ScreenReaderText,
  StyledBreakdownButton,
  StyledBreakdownIcon,
  StyledBreakdownValue
} from 'components/shared/FeeBreakdownModal/FeeBreakdownModal.styles';
import { StyledAmount, StyledCostRow, StyledAlertWrapper, StyledAlertParagraph } from './CostOfCancellation.styles';
import { getFeeBreakdown as getCancellationFeeBreakdown } from 'utils/feeBreakdown';

const CostOfCancellation = ({
  pendingCancellationLegs,
  pendingCreationLegs,
  totalPriceToBePaid,
  totalPoints,
  currency,
  canOverrideFees,
  overrideFees,
  reference,
  isBeingRepriced,
  hotels,
  hasHotelFees,
  hotelCancellationFees,
  hotelMarginCancellationFees,
  trainMarginCancellationFees,
  isLoyalty,
  user,
  customerEmail
}) => {
  const { MARGIN_TRAIN } = marginTariff;

  const ticketTotal = getTicketTotal(pendingCancellationLegs);
  const trainFees = getTrainFees(pendingCancellationLegs);
  const trainLoyaltyPoints = getTrainLoyaltyPoints(pendingCancellationLegs);
  const dbFeeAmount = getDbFeeAmount(pendingCancellationLegs);
  const trainFeesExclDbFee = trainFees + dbFeeAmount;
  const extrasTotal = getExtrasTotal(pendingCancellationLegs);
  const extrasFees = getExtrasFees(pendingCancellationLegs);
  const showExtrasPrices = extrasTotal > 0;

  const newTicketTotal = getProvisionalTicketTotal(pendingCreationLegs);

  const deprecatedTrainMargin = getTrainDeprecatedMargin(hotels);

  const trainMargin = getExtrasWithCode(pendingCancellationLegs, MARGIN_TRAIN);
  // There is no margin_train product, the agen will add a fee manually
  const trainMarginFees = deprecatedTrainMargin > 0 ? 0 : getFeeAmount(trainMarginCancellationFees);

  const hotelsTotal = getHotelsPrice(hotels);
  const hotelsFees = getFeeAmount(hotelCancellationFees);

  const showHotelsPrices = hotelsTotal > 0;

  const hotelMargin = getHotelsMargin(hotels);
  const hotelsMarginFee = deprecatedTrainMargin > 0 ? 0 : getFeeAmount(hotelMarginCancellationFees);

  const ticketTotalWithMargin = deprecatedTrainMargin > 0 ? ticketTotal : ticketTotal + trainMargin;
  const hotelTotalWithMargin = deprecatedTrainMargin > 0 ? hotelsTotal : hotelsTotal + hotelMargin;

  const getProvisionalFees = propOr(0, 'sumOfProvisionalFees');

  const totalFeesToOverride = pipe(map(getProvisionalFees), sum)(pendingCancellationLegs);

  const isExchangeNonEurostarSummatedFareOnly =
    pendingCancellationLegs?.length > 0 &&
    pendingCancellationLegs.every(({ isSummatedFare, serviceType }) => isSummatedFare && !EUROSTAR_CARRIERS.includes(serviceType?.code));

  const nonEurostarExchangeFeesPrice =
    pendingCancellationLegs?.length > 0
      ? pendingCancellationLegs.reduce((acc, { isSummatedFare, serviceType, fees }) => {
          if (isSummatedFare && !EUROSTAR_CARRIERS.includes(serviceType?.code)) {
            return (
              acc +
              fees.reduce((sum, { price, cancelled }) => {
                if (!cancelled) {
                  return sum + price;
                }

                return sum;
              }, 0)
            );
          }

          return acc;
        }, 0)
      : 0;

  const hasFees = hasHotelFees
    ? totalFeesToOverride > 0
    : totalFeesToOverride > 0 &&
      !(nonEurostarExchangeFeesPrice.toFixed(2) === totalFeesToOverride.toFixed(2) || isExchangeNonEurostarSummatedFareOnly);

  const cancFees = Math.abs(trainMarginFees) + Math.abs(trainFeesExclDbFee) + Math.abs(hotelsMarginFee) + Math.abs(hotelsFees);
  const trainCancellationFees = Math.abs(trainMarginFees) + Math.abs(trainFeesExclDbFee);
  const cancellationFeeBreakdown = useMemo(() => getCancellationFeeBreakdown(pendingCancellationLegs), [pendingCancellationLegs]);
  const shouldShowCancellationFeeBreakdown =
    Math.abs(trainFeesExclDbFee) + Math.abs(trainMarginFees) > 0 && cancellationFeeBreakdown.length > 0;

  const shouldShowCancellationFeeTotal = showHotelsPrices || showExtrasPrices;

  const showWaiveFeesWarning = cancellationFeeBreakdown.some((s) =>
    s.carriers.some((c) => !EUROSTAR_CARRIERS.includes(c.key) && c.total > 0)
  );
  const [isActive, setIsActive] = useState(true);
  const [isBreakdownOpen, setIsBreakdownOpen] = useState(false);

  const feeBreakdown = [
    {
      id: 'amount-paid-trains',
      translationId: 'amountPaidTrains',
      value: <PricesAndPointsSummary price={ticketTotalWithMargin} currency={currency} isLoyalty={isLoyalty} points={trainLoyaltyPoints} />
    },
    ...(showHotelsPrices
      ? [
          {
            id: 'amount-paid-hotel',
            translationId: 'amountPaidHotel',
            value: <PricesAndPointsSummary price={hotelTotalWithMargin} currency={currency} isLoyalty={isLoyalty} />
          }
        ]
      : []),
    ...(showExtrasPrices
      ? [
          {
            id: 'amount-paid-extras',
            translationId: 'amountPaidExtras',
            value: <PricesAndPointsSummary price={extrasTotal} currency={currency} />
          }
        ]
      : []),

    ...(isBeingRepriced
      ? [
          {
            id: 'amount-paid-reprice',
            translationId: 'amountPaidReprice',
            value: <FormattedCurrency amount={newTicketTotal * -1} currency={currency} />
          }
        ]
      : []),
    {
      id: 'trains-fee',
      translationId: 'cancellationFeeTrains',
      value: <FormattedCurrency amount={-trainCancellationFees} currency={currency} />
    },
    ...(showHotelsPrices
      ? [
          {
            id: 'hotel-fee',
            translationId: 'cancellationFeeHotel',
            value: <FormattedCurrency amount={-(hotelsFees + hotelsMarginFee)} currency={currency} />
          }
        ]
      : []),
    ...(showExtrasPrices
      ? [
          {
            id: 'extras-fee',
            translationId: 'cancellationFeeExtras',
            value: <FormattedCurrency amount={extrasFees} currency={currency} />
          }
        ]
      : []),
    ...(shouldShowCancellationFeeTotal
      ? [
          {
            id: 'cancellation-fee',
            translationId: 'cancellationFee',
            value: <FormattedCurrency amount={-cancFees} currency={currency} />
          }
        ]
      : []),
    ...(dbFeeAmount > 0
      ? [
          {
            id: 'db-fee',
            translationId: 'dbVoucher',
            value: <FormattedCurrency amount={dbFeeAmount} currency={currency} />
          }
        ]
      : []),
    {
      id: 'refund-amount',
      translationId: totalPriceToBePaid > 0 ? 'paymentAmount' : 'refundAmount',
      value: <PricesAndPointsSummary price={Math.abs(totalPriceToBePaid)} currency={currency} isLoyalty={isLoyalty} points={totalPoints} />
    }
  ];

  if (feeBreakdown.find((fb) => fb.id === 'trains-fee')) {
    feeBreakdown.find((fb) => fb.id === 'trains-fee').value = (
      <StyledBreakdownValue>
        <FormattedCurrency amount={-trainCancellationFees} currency={currency} />
        {shouldShowCancellationFeeBreakdown && (
          <StyledBreakdownButton data-testid="cancellation-fee-breakdown-button" type="button" onClick={() => setIsBreakdownOpen(true)}>
            <StyledBreakdownIcon size={16} />
            <ScreenReaderText>
              <FormattedMessage id="journeySegment.cancellationFees" />
            </ScreenReaderText>
          </StyledBreakdownButton>
        )}
      </StyledBreakdownValue>
    );
  } else {
    feeBreakdown.find((fb) => fb.id === 'cancellation-fee').value = (
      <StyledBreakdownValue>
        <FormattedCurrency amount={-cancFees} currency={currency} />
        {shouldShowCancellationFeeBreakdown && (
          <StyledBreakdownButton data-testid="cancellation-fee-breakdown-button" type="button" onClick={() => setIsBreakdownOpen(true)}>
            <StyledBreakdownIcon size={16} />
            <ScreenReaderText>
              <FormattedMessage id="journeySegment.cancellationFees" />
            </ScreenReaderText>
          </StyledBreakdownButton>
        )}
      </StyledBreakdownValue>
    );
  }

  return (
    <>
      <SectionHeaderBar>
        <SectionHeaderTitle
          data-cy="cost-of-cancellation-section-header-title"
          titleId="journeySegment.costOfCancellation"
          isActive={isActive}
          setIsActive={setIsActive}
        />
        <StyleHeaderItems className="booking-total">
          <FormattedMessage id="summary.total" />
          <StyleHeaderItem data-cy="summary-price">
            <FormattedCurrency amount={totalPriceToBePaid} currency={currency} />
          </StyleHeaderItem>
        </StyleHeaderItems>
      </SectionHeaderBar>
      <StyledBody data-cy="cost-of-cancellation" hide={!isActive}>
        <OverrideFeesCta
          title=""
          showLink={canOverrideFees && !isTravelAgent(user) && (hasFees || isBeingRepriced)}
          onSubmit={(formState) => {
            overrideFees({
              variables: {
                bookingReference: reference,
                overrideReason: formatReason(formState.reasonCode, formState.additionalExplanation, formState.label),
                waiveRepricing: formState.repricing,
                waiveHotelFees: formState.hotelCancellationFee,
                waiveTrainFees: formState.cancellationFee
              }
            });
          }}
          includeReason
          includeCancellationFee={hasFees}
          includeHotelCancellationFee={hasHotelFees}
          includeRepricing={isBeingRepriced}
          waiveFeesWarning={showWaiveFeesWarning && <FormattedMessage id="overrideFees.waiveFeesWarning" />}
        />
        {feeBreakdown.map((fee) => (
          <StyledCostRow key={fee.id}>
            <Column span={5}>
              <FormattedMessage id={`journeySegment.${fee.translationId}`} />:
            </Column>
            <Column span={3}>
              <StyledAmount id={fee.id} data-cy={fee.id} data-testid={fee.id}>
                {fee.value}
              </StyledAmount>
            </Column>
          </StyledCostRow>
        ))}
        {dbFeeAmount > 0 && (
          <StyledAlertWrapper>
            <AlertBox variant={AlertBox.variants.WARNING} dataCy="db-fee-warning">
              <StyledAlertParagraph>
                <strong>
                  <FormattedMessage id="dbVoucher.title" />
                </strong>
              </StyledAlertParagraph>
              <StyledAlertParagraph>
                <FormattedMessage
                  id="dbVoucher.voucherAmount"
                  values={{
                    feeAmount: (
                      <strong>
                        <FormattedCurrency amount={dbFeeAmount} currency={currency} />
                      </strong>
                    )
                  }}
                />
              </StyledAlertParagraph>
              <StyledAlertParagraph>
                <FormattedMessage
                  id="dbVoucher.voucherDetails"
                  values={{
                    email: <strong>{customerEmail}</strong>
                  }}
                />
              </StyledAlertParagraph>
              {Math.abs(totalPriceToBePaid) > 0 && (
                <>
                  <StyledAlertParagraph>
                    <FormattedMessage id="dbVoucher.refundTotal" />
                  </StyledAlertParagraph>
                  <StyledAlertParagraph>
                    <FormattedMessage
                      id="dbVoucher.refundVoyager"
                      values={{
                        refundAmount: (
                          <strong>
                            <FormattedCurrency amount={Math.abs(totalPriceToBePaid)} currency={currency} />
                          </strong>
                        )
                      }}
                    />
                  </StyledAlertParagraph>
                </>
              )}
            </AlertBox>
          </StyledAlertWrapper>
        )}
      </StyledBody>
      {shouldShowCancellationFeeBreakdown && (
        <FeeBreakdownModal
          isOpen={isBreakdownOpen}
          onClose={() => setIsBreakdownOpen(false)}
          testIdPrefix="cancellation-fee"
          titleMessageId="journeySegment.cancellationFees"
          amount={trainCancellationFees}
          currency={currency}
          carrierMessageId="journeySegment.serviceTypeCancellationFee"
          sections={cancellationFeeBreakdown}
        />
      )}
    </>
  );
};
CostOfCancellation.propTypes = {
  pendingCancellationLegs: PropTypes.arrayOf(legShape),
  pendingCreationLegs: PropTypes.arrayOf(legShape),
  totalPriceToBePaid: PropTypes.number,
  totalPoints: PropTypes.number,
  currency: PropTypes.string,
  canOverrideFees: PropTypes.bool,
  overrideFees: PropTypes.func,
  reference: PropTypes.string,
  isBeingRepriced: PropTypes.bool,
  hotelCancellationFees: PropTypes.arrayOf(feeShape),
  hotelMarginCancellationFees: PropTypes.arrayOf(feeShape),
  trainMarginCancellationFees: PropTypes.arrayOf(feeShape),
  hasHotelFees: PropTypes.bool,
  isLoyalty: PropTypes.bool,
  hotels: hotelsShape,
  user: userShape,
  customerEmail: PropTypes.string
};

export default CostOfCancellation;
